/**
 * BrandIdentityEditor — edit a publication's brand identity.
 *
 * S-065 / FEAT-006 (Phase 5). Route: /settings/brand/:publicationId.
 *
 * What it does:
 *   - Reads publication via GET /publications/:id, then reads the linked
 *     brand identity via GET /brand-identities/:id (publication carries
 *     the brand_identity_id FK).
 *   - Form-based editor (no live preview — the brand fields don't
 *     compose into a viewable artifact the way voice content does).
 *   - Sections:
 *       Identity — name
 *       Colors — primary_palette + secondary_palette as hex inputs with
 *         <input type="color"> swatches kept in sync with text inputs.
 *       Image style — default_image_style enum select, allowed_image_styles
 *         multi-select via checkboxes.
 *       Visual style — image_treatment (textarea), photography_style
 *         (textarea), color_rules (rule list).
 *       Logo — file input; on select, upload to POST /files immediately,
 *         display preview, stage the returned UUID in form state. Saved
 *         only when the user clicks Save (avoids orphan R2 uploads if
 *         they navigate away after upload).
 *
 * Logo upload contract:
 *   - POST /files takes multipart/form-data with field `file`.
 *   - Response: `{ id, content_type, size, url }`. We store `id` as
 *     `logo_file_id` per the schema (db/schema.ts:311 — uuid column,
 *     no Postgres FK to a files table; R2 owns the object).
 *   - The `url` returned is `/assets/<id>`; we render the preview by
 *     hitting `${VITE_API_URL}/assets/<id>` directly (the /assets route
 *     has no auth and serves with sharp transforms on demand).
 *
 * Two-stage save vs upload-and-save:
 *   - The file uploads to R2 the moment the user picks it (so we can
 *     show the preview immediately and report failure inline).
 *   - The brand_identity row is only patched when the user clicks Save.
 *   - Trade-off: if a user uploads, then navigates away without saving,
 *     an orphan R2 object survives. Acceptable for v1 — operator can
 *     clean up via R2 admin if needed. A garbage-collector that prunes
 *     unreferenced uploads belongs in a future story.
 *
 * Schema mapping (db/schema.ts → API):
 *   - name (text)
 *   - primary_palette (jsonb array of color tokens, cap 50, per-entry 32 chars)
 *   - secondary_palette (jsonb array, same caps)
 *   - typography (jsonb object) — exposed as freeform textarea JSON; the API
 *     accepts z.record(z.unknown()) so any shape is valid
 *   - color_rules (text array, max 50 strings of <=500 chars)
 *   - default_image_style (enum, see IMAGE_STYLES)
 *   - allowed_image_styles (array of enums)
 *   - image_treatment (text, longContent cap 20k)
 *   - photography_style (text, longContent cap 20k)
 *   - logo_file_id (uuid)
 *
 * Not exposed in v1:
 *   - text_on_image_rules (jsonb) — needs a shape-aware UI we don't have yet
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  API_BASE_URL,
  ApiError,
  useApiMutation,
  useApiQuery,
} from "@/lib/api";
import { useAuth } from "@clerk/clerk-react";
import { ModalShell } from "@/components/SettingsModalShell";
import { markdownToHtml } from "@/lib/markdown";

interface Publication {
  id: number;
  name: string;
  slug: string;
  brandIdentityId?: number | null;
  brand_identity_id?: number | null;
}

// BUG-039 — structured color roles, not an unordered array. All optional:
// a brand identity may define only the roles it's settled on. Matches
// apps/api/src/pipeline/pipeline-api-client.ts's BrandPalette exactly.
export interface Palette {
  background?: string;
  surface?: string;
  text?: string;
  heading?: string;
  accent?: string;
  link?: string;
}

const PALETTE_ROLES: ReadonlyArray<{ key: keyof Palette; label: string; hint: string }> = [
  { key: "background", label: "Background", hint: "Page/email background" },
  { key: "surface", label: "Surface", hint: "Card/content-area background" },
  { key: "text", label: "Text", hint: "Body text" },
  { key: "heading", label: "Heading", hint: "Falls back to Text if unset" },
  { key: "accent", label: "Accent", hint: "Buttons, highlights, dividers" },
  { key: "link", label: "Link", hint: "Falls back to Accent if unset" },
];

function paletteEqual(a: Palette, b: Palette): boolean {
  return PALETTE_ROLES.every((r) => (a[r.key] ?? "") === (b[r.key] ?? ""));
}

/** Normalizes hex values and drops empty/unset roles entirely — an unset
 * role means "let the formatter fall back," not "store an empty string." */
function normalizePalette(p: Palette): Palette {
  const out: Palette = {};
  for (const { key } of PALETTE_ROLES) {
    const v = normalizeHex(p[key] ?? "");
    if (v.length > 0) out[key] = v;
  }
  return out;
}

type LogoPlacement = "top" | "bottom";

interface BrandIdentity {
  id: number;
  name: string;
  primaryPalette?: Palette;
  primary_palette?: Palette;
  secondaryPalette?: Palette;
  secondary_palette?: Palette;
  typography?: Record<string, unknown>;
  colorRules?: string[];
  color_rules?: string[];
  defaultImageStyle?: string;
  default_image_style?: string;
  allowedImageStyles?: string[];
  allowed_image_styles?: string[];
  imageTreatment?: string | null;
  image_treatment?: string | null;
  photographyStyle?: string | null;
  photography_style?: string | null;
  logoFileId?: string | null;
  logo_file_id?: string | null;
  // BUG-040
  headerImageFileId?: string | null;
  header_image_file_id?: string | null;
  footerImageFileId?: string | null;
  footer_image_file_id?: string | null;
  logoPlacement?: LogoPlacement;
  logo_placement?: LogoPlacement;
  // FEAT-008 / S-121
  sourceMarkdown?: string | null;
  source_markdown?: string | null;
}

interface UpdateBody {
  name?: string;
  primary_palette?: Palette;
  secondary_palette?: Palette;
  typography?: Record<string, unknown>;
  color_rules?: string[];
  default_image_style?: string;
  allowed_image_styles?: string[];
  image_treatment?: string | null;
  photography_style?: string | null;
  logo_file_id?: string | null;
  // BUG-040
  header_image_file_id?: string | null;
  footer_image_file_id?: string | null;
  logo_placement?: LogoPlacement;
  // FEAT-008 / S-121
  source_markdown?: string | null;
}

interface FileUploadResponse {
  id: string;
  content_type: string;
  size: number;
  url: string;
}

const IMAGE_STYLES = [
  "material_design",
  "watercolor_soft",
  "editorial_ink_sketch",
  "bold_geometric",
  "notion_slack",
  "editorial_collage",
] as const;
type ImageStyle = (typeof IMAGE_STYLES)[number];

const IMAGE_STYLE_LABELS: Record<ImageStyle, string> = {
  material_design: "Material Design",
  watercolor_soft: "Watercolor (soft)",
  editorial_ink_sketch: "Editorial ink sketch",
  bold_geometric: "Bold geometric",
  notion_slack: "Notion / Slack",
  editorial_collage: "Editorial collage",
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
function normalizeHex(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}
function isValidHex(input: string): boolean {
  return HEX_RE.test(input.trim());
}

function brandIdOf(p: Publication): number | null {
  return p.brandIdentityId ?? p.brand_identity_id ?? null;
}

function readField<T>(
  row: BrandIdentity,
  camel: keyof BrandIdentity,
  snake: keyof BrandIdentity,
  fallback: T
): T {
  const v = (row[camel] ?? row[snake]) as T | undefined;
  return (v ?? fallback) as T;
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

interface FormState {
  name: string;
  primaryPalette: Palette;
  secondaryPalette: Palette;
  typographyText: string; // user edits as JSON text; parsed at save
  colorRules: string[];
  defaultImageStyle: ImageStyle;
  allowedImageStyles: ImageStyle[];
  imageTreatment: string;
  photographyStyle: string;
  logoFileId: string | null;
  // BUG-040
  headerImageFileId: string | null;
  footerImageFileId: string | null;
  logoPlacement: LogoPlacement;
  // FEAT-008 / S-121
  sourceMarkdown: string;
}

function rowToForm(row: BrandIdentity): FormState {
  const typography = readField<Record<string, unknown>>(
    row,
    "typography",
    "typography",
    {}
  );
  const defaultStyle = (readField<string>(
    row,
    "defaultImageStyle",
    "default_image_style",
    "material_design"
  ) || "material_design") as ImageStyle;
  const allowedStyles = readField<string[]>(
    row,
    "allowedImageStyles",
    "allowed_image_styles",
    [defaultStyle]
  ).filter((s): s is ImageStyle =>
    (IMAGE_STYLES as readonly string[]).includes(s)
  );

  return {
    name: row.name ?? "",
    primaryPalette: readField<Palette>(
      row,
      "primaryPalette",
      "primary_palette",
      {}
    ),
    secondaryPalette: readField<Palette>(
      row,
      "secondaryPalette",
      "secondary_palette",
      {}
    ),
    typographyText:
      Object.keys(typography).length > 0
        ? JSON.stringify(typography, null, 2)
        : "",
    colorRules: readField<string[]>(row, "colorRules", "color_rules", []),
    defaultImageStyle: defaultStyle,
    allowedImageStyles:
      allowedStyles.length > 0 ? allowedStyles : [defaultStyle],
    imageTreatment:
      readField<string | null>(row, "imageTreatment", "image_treatment", "") ??
      "",
    photographyStyle:
      readField<string | null>(
        row,
        "photographyStyle",
        "photography_style",
        ""
      ) ?? "",
    logoFileId:
      readField<string | null>(row, "logoFileId", "logo_file_id", null) ?? null,
    headerImageFileId:
      readField<string | null>(row, "headerImageFileId", "header_image_file_id", null) ?? null,
    footerImageFileId:
      readField<string | null>(row, "footerImageFileId", "footer_image_file_id", null) ?? null,
    logoPlacement:
      readField<LogoPlacement>(row, "logoPlacement", "logo_placement", "top") ?? "top",
    // FEAT-008 / S-121
    sourceMarkdown:
      readField<string | null>(row, "sourceMarkdown", "source_markdown", null) ?? "",
  };
}

// ---------- Top-level view ----------

export function BrandIdentityEditor() {
  const { publicationId } = useParams();
  const pubId = Number(publicationId);

  const pubQ = useApiQuery<Publication>(
    ["publication", pubId],
    `/publications/${pubId}`,
    { enabled: Number.isFinite(pubId) }
  );

  const brandId = pubQ.data ? brandIdOf(pubQ.data) : null;

  const brandQ = useApiQuery<BrandIdentity>(
    ["brand-identity", brandId ?? 0],
    `/brand-identities/${brandId ?? 0}`,
    { enabled: brandId != null }
  );

  if (!Number.isFinite(pubId)) {
    return (
        <div className="settings-page">
          <div className="state-error">Invalid publication id.</div>
        </div>
    );
  }

  if (pubQ.isLoading || (brandId != null && brandQ.isLoading)) {
    return (
        <div className="settings-page">
          <div className="state-loading">Loading brand identity…</div>
        </div>
    );
  }

  if (pubQ.error) {
    return (
        <div className="settings-page">
          <div className="state-error">
            {pubQ.error instanceof ApiError && pubQ.error.status === 404
              ? "Publication not found."
              : (pubQ.error.message ?? "Could not load publication.")}
          </div>
        </div>
    );
  }

  if (!pubQ.data) {
    return (
        <div className="settings-page">
          <div className="state-error">Publication not found.</div>
        </div>
    );
  }

  if (brandId == null) {
    return (
        <div className="settings-page">
          <div className="state-error">
            This publication has no brand identity attached. Open{" "}
            <Link to="/settings/publications" className="settings-link">
              Publications
            </Link>{" "}
            to attach one.
          </div>
        </div>
    );
  }

  if (brandQ.error) {
    return (
        <div className="settings-page">
          <div className="state-error">
            {brandQ.error.message ?? "Could not load brand identity."}
          </div>
        </div>
    );
  }

  if (!brandQ.data) return null;

  return <BrandEditorBody publication={pubQ.data} brand={brandQ.data} />;
}

// ---------- Editor body ----------

/** Local-only upload bookkeeping shared by the logo/header/footer image slots — revokes its object URL on unmount or replacement. */
function useImageUploadState() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);
  function clearPreview() {
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(null);
  }
  return { uploading, setUploading, error, setError, localPreview, setLocalPreview, clearPreview };
}

interface BodyProps {
  publication: Publication;
  brand: BrandIdentity;
}

function BrandEditorBody({ publication, brand }: BodyProps) {
  const qc = useQueryClient();
  const initial = useMemo(() => rowToForm(brand), [brand]);
  const [form, setForm] = useState<FormState>(initial);
  useEffect(() => {
    setForm(rowToForm(brand));
  }, [brand]);

  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Phase 5 UX polish (2026-05-25): transient inline "Saved" badge,
  // same pattern as VoiceProfileEditor.
  const [recentlySaved, setRecentlySaved] = useState(false);
  const [typographyJsonError, setTypographyJsonError] = useState<string | null>(
    null
  );

  // Upload state (local-only — the file_id itself stays in form state).
  // One instance per image slot: logo, plus BUG-040's header/footer banners.
  const logoUpload = useImageUploadState();
  const headerUpload = useImageUploadState();
  const footerUpload = useImageUploadState();

  // BUG-039 — Kit needs a one-time manual template setup on Kit's own
  // side to pick up brand colors; this modal walks through it.
  const [showKitHowTo, setShowKitHowTo] = useState(false);

  const dirty =
    form.name !== initial.name ||
    form.imageTreatment !== initial.imageTreatment ||
    form.photographyStyle !== initial.photographyStyle ||
    form.defaultImageStyle !== initial.defaultImageStyle ||
    form.typographyText !== initial.typographyText ||
    form.logoFileId !== initial.logoFileId ||
    form.headerImageFileId !== initial.headerImageFileId ||
    form.footerImageFileId !== initial.footerImageFileId ||
    form.logoPlacement !== initial.logoPlacement ||
    form.sourceMarkdown !== initial.sourceMarkdown ||
    !paletteEqual(form.primaryPalette, initial.primaryPalette) ||
    !paletteEqual(form.secondaryPalette, initial.secondaryPalette) ||
    !arraysEqual(form.colorRules, initial.colorRules) ||
    !arraysEqual(form.allowedImageStyles, initial.allowedImageStyles);

  // beforeunload guard while dirty.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const mutation = useApiMutation<BrandIdentity, UpdateBody>(
    `/brand-identities/${brand.id}`,
    "PATCH",
    {
      onSuccess: () => {
        setSavedAt(new Date());
        setRecentlySaved(true);
        // Local previews are no longer needed once the server confirms.
        logoUpload.clearPreview();
        headerUpload.clearPreview();
        footerUpload.clearPreview();
        // Phase 5 UX polish (2026-05-25): invalidate the brand-identity
        // query so the baseline `initial` recomputes from fresh server
        // data and the dirty indicator clears after save.
        void qc.invalidateQueries({ queryKey: ["brand-identity", brand.id] });
      },
    }
  );

  // Auto-clear the inline "Saved" badge after a short delay.
  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  function buildPatch(): UpdateBody | null {
    const body: UpdateBody = {};
    if (form.name.trim() !== initial.name) body.name = form.name.trim();
    if (form.imageTreatment !== initial.imageTreatment) {
      body.image_treatment = form.imageTreatment.trim() || null;
    }
    if (form.photographyStyle !== initial.photographyStyle) {
      body.photography_style = form.photographyStyle.trim() || null;
    }
    if (form.defaultImageStyle !== initial.defaultImageStyle) {
      body.default_image_style = form.defaultImageStyle;
    }
    if (form.logoFileId !== initial.logoFileId) {
      body.logo_file_id = form.logoFileId;
    }
    if (form.headerImageFileId !== initial.headerImageFileId) {
      body.header_image_file_id = form.headerImageFileId;
    }
    if (form.footerImageFileId !== initial.footerImageFileId) {
      body.footer_image_file_id = form.footerImageFileId;
    }
    if (form.logoPlacement !== initial.logoPlacement) {
      body.logo_placement = form.logoPlacement;
    }
    if (!paletteEqual(form.primaryPalette, initial.primaryPalette)) {
      body.primary_palette = normalizePalette(form.primaryPalette);
    }
    if (!paletteEqual(form.secondaryPalette, initial.secondaryPalette)) {
      body.secondary_palette = normalizePalette(form.secondaryPalette);
    }
    if (!arraysEqual(form.colorRules, initial.colorRules)) {
      body.color_rules = form.colorRules.filter((r) => r.trim().length > 0);
    }
    if (!arraysEqual(form.allowedImageStyles, initial.allowedImageStyles)) {
      body.allowed_image_styles = form.allowedImageStyles;
    }
    if (form.typographyText !== initial.typographyText) {
      const trimmed = form.typographyText.trim();
      if (trimmed.length === 0) {
        body.typography = {};
      } else {
        try {
          const parsed = JSON.parse(trimmed);
          if (typeof parsed !== "object" || parsed == null || Array.isArray(parsed)) {
            setTypographyJsonError(
              "Typography must be a JSON object (e.g. {\"headings\":\"Inter\"})."
            );
            return null;
          }
          body.typography = parsed as Record<string, unknown>;
        } catch {
          setTypographyJsonError("Typography must be valid JSON.");
          return null;
        }
      }
    }
    setTypographyJsonError(null);
    if (form.sourceMarkdown !== initial.sourceMarkdown) {
      body.source_markdown = form.sourceMarkdown.trim() || null;
    }
    return body;
  }

  function handleSave() {
    if (!dirty || mutation.isPending) return;
    const body = buildPatch();
    if (!body) return;
    mutation.mutate(body);
  }

  return (
    <>
      <div className="settings-page editor-page">
        <header className="editor-header">
          {/* Phase 5 UX polish (2026-05-25): explicit Back link in
              addition to the breadcrumb. Mirrors VoiceProfileEditor. */}
          <Link to="/settings/brand" className="editor-back-link">
            ← Back to brand identities
          </Link>
          <nav className="editor-breadcrumb" aria-label="Breadcrumb">
            <Link to="/settings/brand">Settings · Brand Identity</Link>
            <span className="editor-breadcrumb-sep">/</span>
            <span>{publication.name}</span>
            <span className="editor-breadcrumb-sep">/</span>
            <span>Brand identity</span>
          </nav>
          <h1 className="editor-title">
            Brand identity — {publication.name}
          </h1>
          <p className="editor-sub">
            Visual identity for AI-generated images and downstream
            publishing surfaces.
          </p>
        </header>

        <section className="brand-form">
          <div className="brand-section">
            <h2 className="brand-section-title">Identity</h2>
            <label className="settings-field">
              <span className="settings-label">Brand name</span>
              <input
                className="settings-input"
                type="text"
                value={form.name}
                maxLength={120}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </label>
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">Color palette</h2>
            <p className="settings-label-hint" style={{ marginTop: -4, marginBottom: 12 }}>
              These colors apply automatically to branded emails (Mailchimp).{" "}
              <button
                type="button"
                className="settings-link"
                style={{ background: "none", border: 0, padding: 0, cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setShowKitHowTo(true)}
              >
                How to configure Kit →
              </button>
            </p>
            <PaletteEditor
              label="Primary palette"
              hint="Used for branded emails and anywhere the platform renders your colors directly."
              palette={form.primaryPalette}
              onChange={(next) =>
                setForm((f) => ({ ...f, primaryPalette: next }))
              }
            />
            <PaletteEditor
              label="Secondary palette"
              hint="Optional — an alternate set of roles for a second context (e.g. a different amplifier)."
              palette={form.secondaryPalette}
              onChange={(next) =>
                setForm((f) => ({ ...f, secondaryPalette: next }))
              }
            />
            <RuleListField
              label="Color rules"
              hint="Free-form usage notes (e.g. 'Primary on dark backgrounds only')."
              rules={form.colorRules}
              onChange={(next) =>
                setForm((f) => ({ ...f, colorRules: next }))
              }
              placeholder="Primary color only on dark surfaces."
            />
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">Typography</h2>
            <label className="settings-field">
              <span className="settings-label">
                Typography{" "}
                <span className="settings-label-hint">
                  JSON object (e.g. {`{"headings":"Inter","body":"Source Serif"}`})
                </span>
              </span>
              <textarea
                className="voice-textarea voice-textarea-short"
                value={form.typographyText}
                onChange={(e) =>
                  setForm((f) => ({ ...f, typographyText: e.target.value }))
                }
                placeholder='{"headings":"Inter","body":"Source Serif"}'
                spellCheck={false}
              />
              {typographyJsonError && (
                <span className="settings-field-error">
                  {typographyJsonError}
                </span>
              )}
            </label>
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">Image style</h2>
            <label className="settings-field">
              <span className="settings-label">Default image style</span>
              <select
                className="settings-select"
                value={form.defaultImageStyle}
                onChange={(e) => {
                  const next = e.target.value as ImageStyle;
                  setForm((f) => ({
                    ...f,
                    defaultImageStyle: next,
                    // Default must always be in allowed.
                    allowedImageStyles: f.allowedImageStyles.includes(next)
                      ? f.allowedImageStyles
                      : [...f.allowedImageStyles, next],
                  }));
                }}
              >
                {IMAGE_STYLES.map((s) => (
                  <option key={s} value={s}>
                    {IMAGE_STYLE_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="settings-field">
              <span className="settings-label">
                Allowed image styles{" "}
                <span className="settings-label-hint">
                  Styles the art director may pick from per piece.
                </span>
              </span>
              <div className="rule-list">
                {IMAGE_STYLES.map((s) => {
                  const checked = form.allowedImageStyles.includes(s);
                  const isDefault = form.defaultImageStyle === s;
                  return (
                    <label
                      key={s}
                      className="settings-toggle"
                      style={{ padding: "6px 0" }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isDefault}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            allowedImageStyles: e.target.checked
                              ? Array.from(
                                  new Set([...f.allowedImageStyles, s])
                                )
                              : f.allowedImageStyles.filter((x) => x !== s),
                          }));
                        }}
                      />
                      <span className="settings-toggle-text">
                        <span className="settings-toggle-title">
                          {IMAGE_STYLE_LABELS[s]}
                          {isDefault && (
                            <span className="pill pill-info" style={{ marginLeft: 8 }}>
                              Default
                            </span>
                          )}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </label>
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">Visual style prompts</h2>
            <label className="settings-field">
              <span className="settings-label">
                Image treatment{" "}
                <span className="settings-label-hint">
                  Feeds AI image generation. Length-capped server-side.
                </span>
              </span>
              <textarea
                className="voice-textarea voice-textarea-short"
                value={form.imageTreatment}
                onChange={(e) =>
                  setForm((f) => ({ ...f, imageTreatment: e.target.value }))
                }
                placeholder="Soft material design accents, generous whitespace, muted secondary palette."
              />
            </label>
            <label className="settings-field">
              <span className="settings-label">Photography style</span>
              <textarea
                className="voice-textarea voice-textarea-short"
                value={form.photographyStyle}
                onChange={(e) =>
                  setForm((f) => ({ ...f, photographyStyle: e.target.value }))
                }
                placeholder="Editorial. Natural light. Wide negative space."
              />
            </label>
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">Logo</h2>
            <BrandImageUploader
              emptyLabel="No logo"
              currentFileId={form.logoFileId}
              localPreview={logoUpload.localPreview}
              uploading={logoUpload.uploading}
              error={logoUpload.error}
              onUploaded={(fileId, localPreview) => {
                logoUpload.setUploading(false);
                logoUpload.setError(null);
                setForm((f) => ({ ...f, logoFileId: fileId }));
                logoUpload.setLocalPreview(localPreview);
              }}
              onUploadStart={() => {
                logoUpload.setUploading(true);
                logoUpload.setError(null);
              }}
              onUploadError={(msg) => {
                logoUpload.setUploading(false);
                logoUpload.setError(msg);
              }}
              onClear={() => {
                setForm((f) => ({ ...f, logoFileId: null }));
                logoUpload.clearPreview();
              }}
              removeLabel="Remove logo"
            />
            {form.logoFileId && (
              <label className="settings-field" style={{ marginTop: 12 }}>
                <span className="settings-label">
                  Logo placement{" "}
                  <span className="settings-label-hint">
                    Where the logo renders relative to the email body
                  </span>
                </span>
                <div className="brand-radio-row">
                  {(["top", "bottom"] as const).map((placement) => (
                    <label key={placement} className="brand-radio-option">
                      <input
                        type="radio"
                        name="logo-placement"
                        checked={form.logoPlacement === placement}
                        onChange={() =>
                          setForm((f) => ({ ...f, logoPlacement: placement }))
                        }
                      />
                      {placement === "top" ? "Top" : "Bottom"}
                    </label>
                  ))}
                </div>
              </label>
            )}
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">
              Header image{" "}
              <span className="settings-label-hint">
                Optional full-width banner above the logo/title
              </span>
            </h2>
            <BrandImageUploader
              emptyLabel="No header image"
              currentFileId={form.headerImageFileId}
              localPreview={headerUpload.localPreview}
              uploading={headerUpload.uploading}
              error={headerUpload.error}
              onUploaded={(fileId, localPreview) => {
                headerUpload.setUploading(false);
                headerUpload.setError(null);
                setForm((f) => ({ ...f, headerImageFileId: fileId }));
                headerUpload.setLocalPreview(localPreview);
              }}
              onUploadStart={() => {
                headerUpload.setUploading(true);
                headerUpload.setError(null);
              }}
              onUploadError={(msg) => {
                headerUpload.setUploading(false);
                headerUpload.setError(msg);
              }}
              onClear={() => {
                setForm((f) => ({ ...f, headerImageFileId: null }));
                headerUpload.clearPreview();
              }}
              removeLabel="Remove header image"
            />
          </div>

          <div className="brand-section">
            <h2 className="brand-section-title">
              Footer image{" "}
              <span className="settings-label-hint">
                Optional full-width banner below the body
              </span>
            </h2>
            <BrandImageUploader
              emptyLabel="No footer image"
              currentFileId={form.footerImageFileId}
              localPreview={footerUpload.localPreview}
              uploading={footerUpload.uploading}
              error={footerUpload.error}
              onUploaded={(fileId, localPreview) => {
                footerUpload.setUploading(false);
                footerUpload.setError(null);
                setForm((f) => ({ ...f, footerImageFileId: fileId }));
                footerUpload.setLocalPreview(localPreview);
              }}
              onUploadStart={() => {
                footerUpload.setUploading(true);
                footerUpload.setError(null);
              }}
              onUploadError={(msg) => {
                footerUpload.setUploading(false);
                footerUpload.setError(msg);
              }}
              onClear={() => {
                setForm((f) => ({ ...f, footerImageFileId: null }));
                footerUpload.clearPreview();
              }}
              removeLabel="Remove footer image"
            />
          </div>
        </section>

        {/* FEAT-008 / S-121 — Source Markdown: collapsed by default.
            Stores the original brand-identity.md verbatim so the operator
            can reference it while refining the structured palette fields. */}
        <section className="brand-section">
          <details className="editor-disclosure">
            <summary className="editor-disclosure-summary">
              Source markdown{" "}
              <span className="settings-label-hint">
                Original imported content — reference while editing structured fields
              </span>
            </summary>
            <div className="editor-disclosure-body">
              <textarea
                className="voice-textarea voice-textarea-source"
                rows={12}
                value={form.sourceMarkdown}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sourceMarkdown: e.target.value }))
                }
                placeholder="Paste or import brand-identity.md verbatim content here."
              />
            </div>
          </details>
        </section>

        <div className="editor-save-bar">
          <div
            className={`editor-save-status ${
              dirty ? "is-dirty" : savedAt ? "is-saved" : ""
            }`}
          >
            {mutation.error
              ? `Save failed: ${mutation.error.message}`
              : dirty
                ? "Unsaved changes"
                : savedAt
                  ? `Saved ${savedAt.toLocaleTimeString()}`
                  : "Up to date"}
          </div>
          <div className="editor-save-actions">
            {recentlySaved && !dirty && !mutation.isPending && (
              <span className="editor-saved-badge" role="status">
                Saved
              </span>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={!dirty || mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Save brand identity"}
            </button>
          </div>
        </div>
      </div>
      {showKitHowTo && <KitTemplateHowToModal onClose={() => setShowKitHowTo(false)} />}
    </>
  );
}

// ---------- Palette editor ----------

interface PaletteEditorProps {
  label: string;
  hint?: string;
  palette: Palette;
  onChange: (next: Palette) => void;
}

// BUG-039 — fixed named roles instead of a repeatable, orderless swatch
// list. Every role is optional: leaving one blank means "let formatters
// (e.g. the branded email template) fall back" — see PALETTE_ROLES'
// fallback-hint text and formatForBrandedEmail's resolvePalette().
function PaletteEditor({ label, hint, palette, onChange }: PaletteEditorProps) {
  return (
    <div className="settings-field">
      <span className="settings-label">
        {label}
        {hint && <span className="settings-label-hint">{hint}</span>}
      </span>
      <div className="brand-palette-list">
        {PALETTE_ROLES.map(({ key, label: roleLabel, hint: roleHint }) => {
          const value = palette[key] ?? "";
          const valid = isValidHex(value);
          return (
            <div className="brand-palette-row" key={key}>
              <input
                type="color"
                className="brand-color-picker"
                value={valid ? value : "#ffffff"}
                onChange={(e) =>
                  onChange({ ...palette, [key]: e.target.value.toUpperCase() })
                }
                aria-label={`Color picker for ${roleLabel}`}
              />
              <span className="brand-palette-role-label" title={roleHint}>
                {roleLabel}
              </span>
              <input
                className="brand-color-hex"
                type="text"
                value={value}
                placeholder="Unset"
                onChange={(e) => onChange({ ...palette, [key]: e.target.value })}
                onBlur={(e) => {
                  // Auto-prefix # on blur for ergonomics.
                  const v = normalizeHex(e.target.value);
                  if (v !== value) onChange({ ...palette, [key]: v });
                }}
              />
              {value.length > 0 && (
                <button
                  type="button"
                  className="rule-remove-btn"
                  aria-label={`Clear ${roleLabel}`}
                  title={`Clear ${roleLabel} — falls back to default`}
                  onClick={() => onChange({ ...palette, [key]: "" })}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Kit email template how-to (BUG-039) ----------
//
// Kit's broadcast API never sends HTML raw — whatever we send in `content`
// gets merged into an ACCOUNT-LEVEL email template (via a {{ message_content
// }} placeholder) that lives in Kit's own dashboard, not something our API
// call controls. We don't support picking a template per broadcast today
// (no email_template_id field anywhere in the Kit integration), so the
// account's DEFAULT template is what wraps every send. Branding a Kit email
// therefore needs a one-time manual step in Kit's own UI — this guide walks
// through it. Mailchimp doesn't need this: it sends our HTML completely raw.

const KIT_TEMPLATE_HOWTO_MD = `
## Configuring your Kit email template

CCM sends the *content* of each broadcast to Kit, but Kit wraps that content
in a template that lives in **your Kit account**, not in CCM. To get your
brand colors and logo into Kit emails, you'll customize that template once,
directly in Kit.

### 1. Open your email templates in Kit

In Kit, go to **Settings → Advanced → Email Templates** (or search
"Email Templates" from the Kit dashboard search bar).

### 2. Create or edit a custom HTML template

Choose **Create HTML Template** (or **Import Code**, if you already have
HTML). This is where your brand colors, fonts, and logo go — CCM has no
visibility into or control over this template.

### 3. Include the required placeholders

Kit needs these exact placeholders somewhere in your template, or emails
will be broken or non-compliant:

- \`{{ message_content }}\` — **required**. This is where CCM's actual
  content gets inserted. Without it, your emails will be empty.
- \`{{ unsubscribe_link }}\` (or \`{{ unsubscribe_url }}\`) — **required**.
  Kit's own compliance rules require this in every template. Kit will not
  warn you if it's missing — check carefully.
- \`{{ address }}\` — **required**. Your physical mailing address, for
  CAN-SPAM compliance.

A minimal example:

\`\`\`html
<div style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;">
  <img src="https://your-logo-url.png" alt="Your Brand" width="160">
  <div>{{ message_content }}</div>
  <hr>
  <p style="font-size:12px;color:#666;">
    {{ address }}<br>
    <a href="{{ unsubscribe_link }}">Unsubscribe</a>
  </p>
</div>
\`\`\`

Use your Primary Palette's colors from this page for backgrounds, text, and
accents — Kit's template editor accepts standard inline CSS.

### 4. Set it as your default template

Once saved, make sure this template is set as your **account default** —
CCM doesn't send a template selection with each broadcast, so whatever's
marked as default in Kit is what every CCM-dispatched email will use.

### 5. Send a test

Kit doesn't preview-check for a missing \`{{ address }}\` placeholder, so
send yourself a real test broadcast from Kit after saving, and check it
lands looking right before your next real CCM dispatch goes out.
`;

function KitTemplateHowToModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell title="Configure your Kit email template" eyebrow="Branding → Kit" onClose={onClose}>
      <div
        className="article-body-md panel-md"
        dangerouslySetInnerHTML={{ __html: markdownToHtml(KIT_TEMPLATE_HOWTO_MD) }}
      />
    </ModalShell>
  );
}

// ---------- Rule list editor (same shape as VoiceProfileEditor's) ----------

interface RuleListFieldProps {
  label: string;
  hint?: string;
  rules: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

function RuleListField({
  label,
  hint,
  rules,
  onChange,
  placeholder,
}: RuleListFieldProps) {
  return (
    <div className="settings-field">
      <span className="settings-label">
        {label}
        {hint && <span className="settings-label-hint">{hint}</span>}
      </span>
      <div className="rule-list">
        {rules.length === 0 && (
          <span className="voice-rules-empty">No rules yet.</span>
        )}
        {rules.map((rule, i) => (
          <div className="rule-row" key={i}>
            <input
              className="settings-input"
              type="text"
              value={rule}
              maxLength={500}
              placeholder={placeholder}
              onChange={(e) => {
                const next = rules.slice();
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              className="rule-remove-btn"
              aria-label={`Remove rule ${i + 1}`}
              title="Remove rule"
              onClick={() => onChange(rules.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-ghost rule-add-btn"
          onClick={() => onChange([...rules, ""])}
        >
          + Add rule
        </button>
      </div>
    </div>
  );
}

// ---------- Brand image uploader (logo, BUG-040's header/footer banners) ----------

interface BrandImageUploaderProps {
  currentFileId: string | null;
  localPreview: string | null;
  uploading: boolean;
  error: string | null;
  onUploaded: (fileId: string, localPreview: string) => void;
  onUploadStart: () => void;
  onUploadError: (msg: string) => void;
  onClear: () => void;
  /** Preview-empty-state + status-line label, e.g. "No logo" / "No header image". */
  emptyLabel: string;
  /** Remove-button label, e.g. "Remove logo" / "Remove header image". */
  removeLabel: string;
}

/**
 * POST /files needs FormData, not the JSON useApi sends by default.
 * The cheapest path is to grab the Clerk JWT directly and call fetch.
 * We deliberately don't add a "bypass mode" to useApi for this — it's
 * the only file-upload site in the SPA today and keeping useApi simple
 * (single JSON code path) is worth a few extra lines here.
 */
function BrandImageUploader({
  currentFileId,
  localPreview,
  uploading,
  error,
  onUploaded,
  onUploadStart,
  onUploadError,
  onClear,
  emptyLabel,
  removeLabel,
}: BrandImageUploaderProps) {
  const { getToken } = useAuth();

  // Show the local object URL while it exists (just-uploaded UX);
  // otherwise fall back to the R2-served asset for the saved file_id.
  const previewSrc =
    localPreview ??
    (currentFileId ? `${API_BASE_URL}/assets/${currentFileId}?width=192` : null);

  async function handleFile(file: File) {
    if (!file) return;
    onUploadStart();
    try {
      const token = await getToken({ template: "ccm-default" });
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE_URL}/files`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        onUploadError(`Upload failed (${res.status}): ${text || "unknown"}`);
        return;
      }
      const data = (await res.json()) as FileUploadResponse;
      const objectUrl = URL.createObjectURL(file);
      onUploaded(data.id, objectUrl);
    } catch (err) {
      onUploadError(
        err instanceof Error ? err.message : "Image upload failed"
      );
    }
  }

  return (
    <div className="brand-logo-row">
      <div className="brand-logo-preview">
        {previewSrc ? (
          <img src={previewSrc} alt={`${emptyLabel.replace(/^No /, "")} preview`} />
        ) : (
          <div className="brand-logo-preview-empty">{emptyLabel}</div>
        )}
      </div>
      <div className="brand-logo-controls">
        <label className="settings-field" style={{ gap: 6 }}>
          <span className="settings-label">
            Upload{" "}
            <span className="settings-label-hint">
              PNG, JPG, WebP, or SVG up to 25 MB. Click Save to apply.
            </span>
          </span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              // Reset the input so re-selecting the same file fires onChange.
              e.target.value = "";
            }}
          />
        </label>
        <div
          className={`brand-logo-status ${
            uploading ? "is-uploading" : error ? "is-error" : ""
          }`}
        >
          {uploading
            ? "Uploading to R2…"
            : error
              ? error
              : currentFileId
                ? localPreview
                  ? "Uploaded — click Save to apply."
                  : `Current: ${currentFileId.slice(0, 8)}…`
                : `${emptyLabel}.`}
        </div>
        {currentFileId && (
          <button
            type="button"
            className="btn-ghost btn-danger"
            style={{ alignSelf: "flex-start" }}
            onClick={onClear}
            disabled={uploading}
          >
            {removeLabel}
          </button>
        )}
      </div>
    </div>
  );
}
